import { useState, useEffect } from 'react';
import { DollarSign, Users, TrendingUp, Calendar, Download, Edit, Save, X, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import {
  getIncomeSettings,
  updateIncomeSettings,
  getAllUserIncomeConfigs,
  saveUserIncomeConfig,
  getAdditionalIncomesForMonth,
  saveAdditionalIncome,
  getAllMonthlyIncomeSummariesForMonth,
  getBusinessHeadMonthlySummary,
  getLeadIncomeBreakdownsForMonth,
  getMonthString,
} from '@/services/incomeService';
import type {
  IncomeSettings,
  UserIncomeConfig,
  MonthlyIncomeSummary,
  BusinessHeadMonthlySummary,
  LeadIncomeBreakdown,
  EmploymentType,
} from '@/types/income';

export function IncomeManagement() {
  const [activeTab, setActiveTab] = useState<'set-income' | 'earnings'>('set-income');
  const [loading, setLoading] = useState(true);
  
  // Settings
  const [incomeSettings, setIncomeSettings] = useState<IncomeSettings | null>(null);
  const [editingBHPayout, setEditingBHPayout] = useState(false);
  const [bhPayoutValue, setBhPayoutValue] = useState('92');
  
  // User Configs
  const [userConfigs, setUserConfigs] = useState<UserIncomeConfig[]>([]);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editConfig, setEditConfig] = useState<Partial<UserIncomeConfig>>({});
  
  // Additional Income
  const [selectedMonth, setSelectedMonth] = useState(getMonthString(new Date()));
  const [additionalIncomes, setAdditionalIncomes] = useState<Map<string, { amount: number; reason: string }>>(new Map());
  
  // Earnings Tab
  const [monthlySummaries, setMonthlySummaries] = useState<MonthlyIncomeSummary[]>([]);
  const [bhSummary, setBhSummary] = useState<BusinessHeadMonthlySummary | null>(null);
  const [leadBreakdowns, setLeadBreakdowns] = useState<LeadIncomeBreakdown[]>([]);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [recalculating, setRecalculating] = useState(false);
  
  // Load initial data
  useEffect(() => {
    loadData();
  }, []);
  
  // Load data for earnings tab when month changes
  useEffect(() => {
    if (activeTab === 'earnings') {
      loadEarningsData();
    }
  }, [selectedMonth, activeTab]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      const [settings, configs] = await Promise.all([
        getIncomeSettings(),
        getAllUserIncomeConfigs(),
      ]);
      
      setIncomeSettings(settings);
      setBhPayoutValue(settings.businessHeadPayoutPercentage.toString());
      setUserConfigs(configs.filter(c => c.isActive));
    } catch (error) {
      console.error('Error loading income data:', error);
      toast.error('Failed to load income settings');
    } finally {
      setLoading(false);
    }
  };
  
  const loadEarningsData = async () => {
    try {
      setLoading(true);
      const [summaries, bh, breakdowns] = await Promise.all([
        getAllMonthlyIncomeSummariesForMonth(selectedMonth),
        getBusinessHeadMonthlySummary(selectedMonth),
        getLeadIncomeBreakdownsForMonth(selectedMonth),
      ]);
      
      setMonthlySummaries(summaries);
      setBhSummary(bh);
      setLeadBreakdowns(breakdowns);
    } catch (error) {
      console.error('Error loading earnings data:', error);
      toast.error('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveBHPayout = async () => {
    try {
      const newPayout = parseFloat(bhPayoutValue);
      if (isNaN(newPayout) || newPayout < 0 || newPayout > 100) {
        toast.error('Please enter a valid percentage (0-100)');
        return;
      }
      
      await updateIncomeSettings(
        { businessHeadPayoutPercentage: newPayout },
        'admin', // TODO: Get actual user ID
        'Admin User'
      );
      
      await loadData();
      setEditingBHPayout(false);
      toast.success('Business Head payout updated successfully');
    } catch (error) {
      console.error('Error updating BH payout:', error);
      toast.error('Failed to update BH payout');
    }
  };
  
  const handleEditUser = (config: UserIncomeConfig) => {
    setEditingUserId(config.userId);
    setEditConfig({
      employmentType: config.employmentType,
      freelancePayoutPercentage: config.freelancePayoutPercentage,
      monthlySalary: config.monthlySalary,
    });
  };
  
  const handleSaveUser = async (userId: string) => {
    try {
      const config = userConfigs.find(c => c.userId === userId);
      if (!config) return;
      
      await saveUserIncomeConfig(
        {
          ...config,
          ...editConfig,
        },
        'admin', // TODO: Get actual user ID
        'Admin User'
      );
      
      await loadData();
      setEditingUserId(null);
      toast.success('User income configuration updated');
    } catch (error) {
      console.error('Error updating user config:', error);
      toast.error('Failed to update user configuration');
    }
  };
  
  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditConfig({});
  };
  
  const handleSaveAdditionalIncomes = async () => {
    try {
      const promises = Array.from(additionalIncomes.entries()).map(([userId, data]) => {
        const user = userConfigs.find(c => c.userId === userId);
        if (!user || data.amount === 0) return null;
        
        return saveAdditionalIncome(
          userId,
          user.userName,
          selectedMonth,
          data.amount,
          data.reason || 'Manual adjustment',
          'admin', // TODO: Get actual user ID
          'Admin User'
        );
      });
      
      await Promise.all(promises.filter(p => p !== null));
      setAdditionalIncomes(new Map());
      toast.success('Additional incomes saved successfully');
    } catch (error) {
      console.error('Error saving additional incomes:', error);
      toast.error('Failed to save additional incomes');
    }
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };
  
  const exportToExcel = () => {
    try {
      if (monthlySummaries.length === 0) {
        toast.error('No data to export');
        return;
      }
      
      // Prepare data for Excel
      const summaryData = monthlySummaries.map(summary => ({
        'User Name': summary.userName,
        'Employment Type': summary.employmentType === 'salaried' ? 'Salaried' : 'Freelance',
        'Total Revenue': summary.totalRevenueGenerated,
        'Total Income': summary.totalIncome,
        'Salary': summary.monthlySalary || 0,
        'Salary Justified': summary.employmentType === 'salaried' 
          ? (summary.isSalaryJustified ? 'Yes' : 'No') 
          : 'N/A',
        'Total Leads': summary.totalLeads,
        'Additional Income': summary.additionalIncome,
      }));
      
      // Add BH summary at the top
      const bhData = bhSummary ? [{
        'User Name': 'BUSINESS HEAD SUMMARY',
        'Employment Type': '',
        'Total Revenue': bhSummary.totalCompanyRevenue,
        'Total Income': bhSummary.finalBHIncome,
        'Salary': '',
        'Salary Justified': '',
        'Total Leads': bhSummary.totalLeads,
        'Additional Income': bhSummary.salaryShortfallCovered,
      }] : [];
      
      const finalData = [...bhData, {}, ...summaryData]; // Empty object creates a blank row
      
      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(finalData);
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Income Report');
      
      // Generate filename
      const filename = `Income_Report_${selectedMonth.replace('-', '_')}.xlsx`;
      
      // Save file
      XLSX.writeFile(wb, filename);
      
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    }
  };
  
  const handleRecalculateIncome = async () => {
    try {
      setRecalculating(true);
      toast.info('Recalculating income for ' + selectedMonth + '...');
      
      // Note: This requires leads data. For now, we'll show a message that
      // calculations happen automatically on disbursement.
      // In a real implementation, you'd fetch leads and pass them here:
      // const leads = await getLeadsForMonth(selectedMonth);
      // await calculateMonthIncomes(selectedMonth, leads, 'admin');
      
      toast.warning('Income calculations happen automatically when leads are disbursed. Manual recalculation requires lead data integration.');
      
      // For now, just reload the data
      await loadEarningsData();
    } catch (error) {
      console.error('Error recalculating income:', error);
      toast.error('Failed to recalculate income');
    } finally {
      setRecalculating(false);
    }
  };
  
  if (loading && userConfigs.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <DollarSign className="h-12 w-12 mx-auto mb-4 text-muted-foreground animate-pulse" />
          <p className="text-sm text-muted-foreground">Loading income management...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Income Management</h2>
        <p className="text-muted-foreground">
          Manage employee compensation, salary structures, and earnings distribution
        </p>
      </div>
      
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="set-income">
            <Users className="h-4 w-4 mr-2" />
            Set Income
          </TabsTrigger>
          <TabsTrigger value="earnings">
            <TrendingUp className="h-4 w-4 mr-2" />
            Earnings / P&L
          </TabsTrigger>
        </TabsList>
        
        {/* SET INCOME TAB */}
        <TabsContent value="set-income" className="space-y-6">
          {/* Business Head Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Business Head Payout Settings</CardTitle>
              <CardDescription>
                Set the Business Head's share of company revenue (default: 92%)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="flex-1 max-w-xs">
                  <Label>BH Payout Percentage</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={bhPayoutValue}
                      onChange={(e) => setBhPayoutValue(e.target.value)}
                      disabled={!editingBHPayout}
                      className="w-24"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                    {!editingBHPayout ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingBHPayout(true)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleSaveBHPayout}
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingBHPayout(false);
                            setBhPayoutValue(incomeSettings?.businessHeadPayoutPercentage.toString() || '92');
                          }}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <Alert>
                    <AlertDescription>
                      Company gets {incomeSettings?.companyPayoutPercentage}% automatically.
                      Employees and freelancers get paid from BH's {incomeSettings?.businessHeadPayoutPercentage}% share.
                    </AlertDescription>
                  </Alert>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* User Income Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>User Income Configuration</CardTitle>
              <CardDescription>
                Set employment type and compensation for each user
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User Name</TableHead>
                    <TableHead>Employment Type</TableHead>
                    <TableHead>Freelance Payout %</TableHead>
                    <TableHead>Monthly Salary</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userConfigs.map((config) => {
                    const isEditing = editingUserId === config.userId;
                    
                    return (
                      <TableRow key={config.userId}>
                        <TableCell className="font-medium">{config.userName}</TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Select
                              value={editConfig.employmentType || config.employmentType}
                              onValueChange={(v: EmploymentType) => 
                                setEditConfig({ ...editConfig, employmentType: v })
                              }
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="salaried">Salaried</SelectItem>
                                <SelectItem value="freelance">Freelance</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant={config.employmentType === 'salaried' ? 'default' : 'secondary'}>
                              {config.employmentType === 'salaried' ? 'Salaried' : 'Freelance'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {(isEditing ? editConfig.employmentType : config.employmentType) === 'freelance' ? (
                            isEditing ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={editConfig.freelancePayoutPercentage || ''}
                                  onChange={(e) => 
                                    setEditConfig({
                                      ...editConfig,
                                      freelancePayoutPercentage: parseFloat(e.target.value)
                                    })
                                  }
                                  className="w-20"
                                />
                                <span className="text-sm">%</span>
                              </div>
                            ) : (
                              <span>{config.freelancePayoutPercentage}%</span>
                            )
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {(isEditing ? editConfig.employmentType : config.employmentType) === 'salaried' ? (
                            isEditing ? (
                              <Input
                                type="number"
                                min="0"
                                step="1000"
                                value={editConfig.monthlySalary || ''}
                                onChange={(e) => 
                                  setEditConfig({
                                    ...editConfig,
                                    monthlySalary: parseFloat(e.target.value)
                                  })
                                }
                                className="w-32"
                              />
                            ) : (
                              formatCurrency(config.monthlySalary || 0)
                            )
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {isEditing ? (
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleSaveUser(config.userId)}
                              >
                                <Save className="h-4 w-4 mr-1" />
                                Save
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleCancelEdit}
                              >
                                <X className="h-4 w-4 mr-1" />
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditUser(config)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          
          {/* Additional Income */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Income / Adjustments</CardTitle>
              <CardDescription>
                Add bonuses, penalties, or manual adjustments for any month
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Label className="w-24">Month:</Label>
                  <Input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="w-48"
                  />
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  {userConfigs.map((config) => {
                    const currentValue = additionalIncomes.get(config.userId);
                    
                    return (
                      <div key={config.userId} className="flex items-center gap-4">
                        <div className="w-48 font-medium">{config.userName}</div>
                        <Input
                          type="number"
                          placeholder="Amount (±)"
                          value={currentValue?.amount || ''}
                          onChange={(e) => {
                            const newMap = new Map(additionalIncomes);
                            newMap.set(config.userId, {
                              amount: parseFloat(e.target.value) || 0,
                              reason: currentValue?.reason || ''
                            });
                            setAdditionalIncomes(newMap);
                          }}
                          className="w-32"
                        />
                        <Input
                          placeholder="Reason"
                          value={currentValue?.reason || ''}
                          onChange={(e) => {
                            const newMap = new Map(additionalIncomes);
                            newMap.set(config.userId, {
                              amount: currentValue?.amount || 0,
                              reason: e.target.value
                            });
                            setAdditionalIncomes(newMap);
                          }}
                          className="flex-1"
                        />
                      </div>
                    );
                  })}
                </div>
                
                <div className="flex justify-end">
                  <Button onClick={handleSaveAdditionalIncomes}>
                    <Save className="h-4 w-4 mr-2" />
                    Save Additional Incomes
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* EARNINGS TAB */}
        <TabsContent value="earnings" className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Label>Month:</Label>
              <Input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-48"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleRecalculateIncome}
                disabled={recalculating}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${recalculating ? 'animate-spin' : ''}`} />
                {recalculating ? 'Calculating...' : 'Recalculate'}
              </Button>
            </div>
            <Button variant="outline" onClick={exportToExcel}>
              <Download className="h-4 w-4 mr-2" />
              Export to Excel
            </Button>
          </div>
          
          {/* Info about automatic calculations */}
          <Alert>
            <TrendingUp className="h-4 w-4" />
            <AlertDescription>
              Income calculations run automatically when leads are disbursed. Use the "Recalculate" button to manually refresh income data for the selected month.
            </AlertDescription>
          </Alert>
          
          {/* Business Head Summary */}
          {bhSummary && (
            <Card>
              <CardHeader>
                <CardTitle>Business Head Summary</CardTitle>
                <CardDescription>Total revenue and income distribution</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Revenue</p>
                    <p className="text-2xl font-bold">{formatCurrency(bhSummary.totalCompanyRevenue)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">BH Income</p>
                    <p className="text-2xl font-bold">{formatCurrency(bhSummary.finalBHIncome)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Shortfall Covered</p>
                    <p className="text-2xl font-bold text-destructive">
                      -{formatCurrency(bhSummary.salaryShortfallCovered)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Leads</p>
                    <p className="text-2xl font-bold">{bhSummary.totalLeads}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* User Earnings Table */}
          <Card>
            <CardHeader>
              <CardTitle>User Earnings</CardTitle>
              <CardDescription>Monthly income breakdown by user</CardDescription>
            </CardHeader>
            <CardContent>
              {monthlySummaries.length === 0 ? (
                <Alert>
                  <AlertDescription>
                    No earnings data for {selectedMonth}. Run income calculation for this month.
                  </AlertDescription>
                </Alert>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">Income</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Leads</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthlySummaries.map((summary) => {
                      const userLeads = leadBreakdowns.filter(
                        lead => lead.assignedUserId === summary.userId
                      );
                      const isExpanded = expandedUserId === summary.userId;
                      
                      return (
                        <Collapsible
                          key={summary.id}
                          open={isExpanded}
                          onOpenChange={(open) => setExpandedUserId(open ? summary.userId : null)}
                          asChild
                        >
                          <>
                            <CollapsibleTrigger asChild>
                              <TableRow className="cursor-pointer hover:bg-muted/50">
                                <TableCell>
                                  {userLeads.length > 0 && (
                                    isExpanded ? (
                                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    )
                                  )}
                                </TableCell>
                                <TableCell className="font-medium">{summary.userName}</TableCell>
                                <TableCell>
                                  <Badge variant={summary.employmentType === 'salaried' ? 'default' : 'secondary'}>
                                    {summary.employmentType === 'salaried' ? 'Salaried' : 'Freelance'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(summary.totalRevenueGenerated)}
                                </TableCell>
                                <TableCell className="text-right font-bold">
                                  {formatCurrency(summary.totalIncome)}
                                </TableCell>
                                <TableCell>
                                  {summary.employmentType === 'salaried' ? (
                                    summary.isSalaryJustified ? (
                                      <Badge variant="default">✓ Justified</Badge>
                                    ) : (
                                      <Badge variant="destructive">✗ Shortfall</Badge>
                                    )
                                  ) : (
                                    <Badge variant="outline">N/A</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">{summary.totalLeads}</TableCell>
                              </TableRow>
                            </CollapsibleTrigger>
                            
                            {userLeads.length > 0 && (
                              <CollapsibleContent asChild>
                                <TableRow>
                                  <TableCell colSpan={7} className="bg-muted/30 p-0">
                                    <div className="px-12 py-4">
                                      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                                        <TrendingUp className="h-4 w-4" />
                                        Lead-wise Breakdown ({userLeads.length} leads)
                                      </h4>
                                      <div className="space-y-2">
                                        {userLeads.map((lead) => (
                                          <div 
                                            key={lead.id}
                                            className="flex items-center justify-between p-3 bg-background rounded-lg border text-sm"
                                          >
                                            <div className="flex-1">
                                              <p className="font-medium">{lead.leadName}</p>
                                              <p className="text-xs text-muted-foreground">
                                                {lead.loanType} • Disbursed: {formatCurrency(lead.disbursedAmount)}
                                              </p>
                                            </div>
                                            <div className="flex items-center gap-6 text-right">
                                              <div>
                                                <p className="text-xs text-muted-foreground">Bank Payout</p>
                                                <p className="font-medium">{formatCurrency(lead.totalBankPayout)}</p>
                                              </div>
                                              <div>
                                                <p className="text-xs text-muted-foreground">Vendor</p>
                                                <p className="font-medium text-destructive">
                                                  -{formatCurrency(lead.vendorPayoutAmount)}
                                                </p>
                                              </div>
                                              <div>
                                                <p className="text-xs text-muted-foreground">User Income</p>
                                                <p className="font-bold text-green-600">
                                                  {formatCurrency(lead.finalEmployeeIncome)}
                                                </p>
                                              </div>
                                              <div>
                                                <p className="text-xs text-muted-foreground">BH Share</p>
                                                <p className="font-medium">{formatCurrency(lead.finalBusinessHeadIncome)}</p>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      
                                      {/* Summary for this user */}
                                      <div className="mt-4 pt-3 border-t flex justify-end gap-8 text-sm">
                                        <div className="text-right">
                                          <p className="text-muted-foreground">Total Revenue</p>
                                          <p className="font-bold">{formatCurrency(summary.totalRevenueGenerated)}</p>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-muted-foreground">User Income</p>
                                          <p className="font-bold text-green-600">
                                            {formatCurrency(summary.totalIncome)}
                                          </p>
                                        </div>
                                        {summary.additionalIncome !== 0 && (
                                          <div className="text-right">
                                            <p className="text-muted-foreground">Adjustments</p>
                                            <p className={`font-bold ${summary.additionalIncome > 0 ? 'text-green-600' : 'text-destructive'}`}>
                                              {summary.additionalIncome > 0 ? '+' : ''}{formatCurrency(summary.additionalIncome)}
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              </CollapsibleContent>
                            )}
                          </>
                        </Collapsible>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
